import React, { useEffect, useState, useCallback } from 'react';
import api from '../../../../utils/api';
import {
  RiCalendarCheckLine, RiTaskLine, RiDraggable, RiTimeLine,
  RiPlayCircleLine, RiCheckboxCircleLine, RiRefreshLine,
} from 'react-icons/ri';

const COLUMNS = [
  { key: 'todo',        label: 'To Do',       icon: RiTimeLine,           color: '#9CA3AF', accent: 'rgba(156,163,175,0.4)' },
  { key: 'in_progress', label: 'In Progress', icon: RiPlayCircleLine,     color: '#0984e3', accent: 'rgba(9,132,227,0.5)'  },
  { key: 'completed',   label: 'Completed',   icon: RiCheckboxCircleLine, color: '#10b981', accent: 'rgba(16,185,129,0.5)' },
];

function formatDue(raw) {
  return raw
    ? new Date(raw).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;
}

function isOverdue(t) {
  const due = t.due_date || t.dueDate;
  if (!due || t.status === 'completed') return false;
  const d = new Date(due);
  const dueStr   = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const n = new Date();
  const todayStr = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
  return dueStr < todayStr;
}

export default function TasksTab() {
  const [tasks,         setTasks]         = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState('');
  const [draggedId,     setDraggedId]     = useState(null);
  const [hoverColumn,   setHoverColumn]   = useState(null);
  const [updatingId,    setUpdatingId]    = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    api.get('/tasks/mine', { params: { page: 1, limit: 100 } })
      .then((res) => {
        const d = res.data;
        const list = Array.isArray(d) ? d : d?.tasks || d?.data || [];
        setTasks(list);
      })
      .catch(() => setError('Failed to load tasks. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Drag handlers ─────────────────────────────────────────────────────────
  const onDragStart = (e, taskId) => {
    setDraggedId(taskId);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', taskId); } catch (_) {}
  };

  const onDragEnd = () => { setDraggedId(null); setHoverColumn(null); };

  const onDragOver = (e, colKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (hoverColumn !== colKey) setHoverColumn(colKey);
  };

  const onDragLeave = (e, colKey) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    if (hoverColumn === colKey) setHoverColumn(null);
  };

  const onDrop = async (e, newStatus) => {
    e.preventDefault();
    setHoverColumn(null);
    const taskId = draggedId || e.dataTransfer.getData('text/plain');
    setDraggedId(null);
    if (!taskId) return;

    const task = tasks.find((t) => (t._id || t.id) === taskId);
    if (!task || task.status === newStatus) return;

    const oldStatus = task.status;
    // Optimistic update
    setTasks((prev) => prev.map((t) => (t._id || t.id) === taskId ? { ...t, status: newStatus } : t));
    setUpdatingId(taskId);
    try {
      await api.put(`/tasks/${taskId}`, { status: newStatus });
    } catch {
      // Revert on failure
      setTasks((prev) => prev.map((t) => (t._id || t.id) === taskId ? { ...t, status: oldStatus } : t));
      setError('Failed to update status. Please try again.');
    } finally {
      setUpdatingId(null);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {COLUMNS.map(({ key }) => (
          <div key={key} className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-2xl p-4 min-h-[400px] space-y-3">
            <div className="h-5 bg-[#E5E7EB] rounded w-2/5 animate-pulse" />
            {[...Array(2)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-3 space-y-2">
                <div className="h-4 bg-[#F3F4F6] rounded w-3/4 animate-pulse" />
                <div className="h-3 bg-[#F3F4F6] rounded w-1/2 animate-pulse" />
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center justify-between">
        {error}
        <button onClick={load} className="text-xs font-semibold hover:underline cursor-pointer ml-4 flex items-center gap-1">
          <RiRefreshLine size={13} /> Retry
        </button>
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="text-center py-16 bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)]">
        <RiTaskLine size={36} className="text-[#E5E7EB] mx-auto mb-3" />
        <p className="text-[#9CA3AF] font-medium">No tasks assigned yet.</p>
        <p className="text-[#9CA3AF] text-xs mt-1">Tasks assigned by admin will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-[#6B7280] text-xs">
          <span className="font-semibold text-[#374151]">Tip:</span> drag a card from one column to another to update its status.
        </p>
        <button
          onClick={load}
          className="flex items-center gap-1.5 text-xs font-semibold text-[#6B7280] hover:text-[#0984e3] cursor-pointer"
        >
          <RiRefreshLine size={13} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {COLUMNS.map((col) => {
          const Icon     = col.icon;
          const colTasks = tasks.filter((t) => t.status === col.key);
          const isHover  = hoverColumn === col.key;

          return (
            <div
              key={col.key}
              onDragOver={(e) => onDragOver(e, col.key)}
              onDragLeave={(e) => onDragLeave(e, col.key)}
              onDrop={(e) => onDrop(e, col.key)}
              className={`bg-[#F8FAFC] border-2 rounded-2xl p-4 min-h-[400px] flex flex-col transition-all ${
                isHover ? 'border-dashed shadow-[0_4px_20px_rgba(9,132,227,0.15)]' : 'border-[#E5E7EB]'
              }`}
              style={isHover ? { borderColor: col.accent, background: '#fff' } : {}}
            >
              {/* Column header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Icon size={16} style={{ color: col.color }} />
                  <h3 className="text-[#1F2937] font-bold text-sm">{col.label}</h3>
                </div>
                <span
                  className="text-xs font-bold px-2 py-0.5 rounded-full"
                  style={{ background: `${col.color}1a`, color: col.color }}
                >
                  {colTasks.length}
                </span>
              </div>

              {/* Cards */}
              <div className="space-y-2.5 flex-1">
                {colTasks.length === 0 ? (
                  <div className="text-center py-8 px-3 border-2 border-dashed border-[#E5E7EB] rounded-xl">
                    <p className="text-[#9CA3AF] text-xs">
                      {isHover ? 'Drop here' : 'No tasks'}
                    </p>
                  </div>
                ) : (
                  colTasks.map((t) => {
                    const id       = t._id || t.id;
                    const isDrag   = draggedId === id;
                    const isUpd    = updatingId === id;
                    const overdue  = isOverdue(t);

                    return (
                      <div
                        key={id}
                        draggable={!isUpd}
                        onDragStart={(e) => onDragStart(e, id)}
                        onDragEnd={onDragEnd}
                        className={`group bg-white border border-[#E5E7EB] rounded-xl p-3 shadow-[0_2px_8px_rgba(15,23,42,0.04)] hover:shadow-[0_4px_16px_rgba(15,23,42,0.08)] transition-all ${
                          isUpd ? 'cursor-wait opacity-70' : 'cursor-grab active:cursor-grabbing'
                        } ${isDrag ? 'opacity-40 scale-95' : ''}`}
                      >
                        <div className="flex items-start gap-2">
                          <RiDraggable size={14} className="text-[#D1D5DB] group-hover:text-[#9CA3AF] mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-semibold ${
                              col.key === 'completed' ? 'line-through text-[#9CA3AF]' : 'text-[#1F2937]'
                            }`}>
                              {t.title}
                            </p>
                            {t.description && (
                              <p className="text-[#6B7280] text-xs leading-relaxed mt-1 line-clamp-2">
                                {t.description}
                              </p>
                            )}
                            {(t.due_date || t.dueDate) && (
                              <span className={`mt-2 text-[11px] flex items-center gap-1 ${
                                overdue ? 'text-red-500 font-semibold' : 'text-[#9CA3AF]'
                              }`}>
                                <RiCalendarCheckLine size={11} />
                                {overdue ? 'Overdue · ' : 'Due '}
                                {formatDue(t.due_date || t.dueDate)}
                              </span>
                            )}
                          </div>
                          {isUpd && (
                            <span className="w-3.5 h-3.5 border-2 border-[#0984e3] border-t-transparent rounded-full animate-spin flex-shrink-0" />
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
